import DocumentPage from './DocumentPage';

export const invoiceDocument = {
  slug: 'invoice',
  route: '/invoice',
  title: 'Invoice',
  category: 'Finance & Procurement',
  format: 'DOCX',
  description:
    'The Invoice module manages customer billing, vendor finance, payment tracking, and accounting configuration from one shared finance workspace.',
  metrics: [
    { label: 'Primary focus', value: 'Billing and payments' },
    { label: 'Core records', value: 'Invoices, bills, payments' },
    { label: 'Coverage', value: 'Customer and vendor finance' },
  ],
  workflow: [
    'Create customer invoices, credit notes, and payment records for sales billing.',
    'Manage vendor bills, refunds, vendor payments, expenses, and master records for suppliers and bank accounts.',
    'Configure payment rules, accounting structures, and online payment settings for ongoing invoicing control.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The Invoice Module in Odoo 17 is used to manage all financial transactions related to customers and vendors. It allows users to create invoices, manage payments, track outstanding amounts, and generate financial reports.',
        'In the EduX system, this module is used to handle student fee invoices, vendor payments, and financial tracking.',
      ],
    },
    {
      id: 'customer-menu',
      title: 'Customer Menu',
      body: [
        'The customer side of the invoice module is used to create bills for customers, manage payment collection, and maintain product and customer masters used in invoicing.',
      ],
      subsections: [
        {
          id: 'invoices',
          title: 'Invoices',
          body: [
            'Go to Invoice -> Customer -> Invoice.',
            'Invoices are used to create bills for customers for products or services provided.',
            'Why we use it: to record sales and track customer payments.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/invoice/image2.png',
              alt: 'Customer invoice list showing posted invoice status',
              caption: 'The invoice list gives a quick overview of invoice number, due date, payment status, and posting state.',
            },
            {
              src: '/assets/doc-images/invoice/image.png',
              alt: 'Customer invoice form with customer and invoice line details',
              caption: 'The invoice form stores billing details, due dates, taxes, and invoice lines for the customer.',
            },
          ],
        },
        {
          id: 'credit-notes',
          title: 'Credit Notes',
          body: [
            'Go to Invoice -> Customer -> Credit Notes.',
            'Credit Notes are used to create refunds or adjustments for customer invoices.',
            'Why we use it: to correct overcharges or return money to customers.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image3.png',
            alt: 'Customer credit note list for invoice adjustments',
            caption: 'Credit notes are used when part or all of an earlier invoice must be reversed.',
          },
        },
        {
          id: 'customer-payments',
          title: 'Payments',
          body: [
            'Go to Invoice -> Customer -> Payments.',
            'Payments record amounts received from customers.',
            'Why we use it: to track paid and unpaid invoices.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image4.png',
            alt: 'Customer payments list in invoice module',
            caption: 'Customer payment records show how invoice collection is being settled.',
          },
        },
        {
          id: 'invoice-products',
          title: 'Products',
          body: [
            'Go to Invoice -> Customer -> Products.',
            'This list contains products or services sold to customers.',
            'Why we use it: to easily add items while creating invoices.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image5.png',
            alt: 'Product form for invoice items such as tuition fee',
            caption: 'Products and services are maintained once and reused during invoice creation.',
          },
        },
        {
          id: 'customers',
          title: 'Customers',
          body: [
            'Go to Invoice -> Customer -> Customers.',
            'This menu stores customer details like name, contact, and address.',
            'Why we use it: to manage customer information for invoicing.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image6.png',
            alt: 'Customer form with contact and invoicing information',
            caption: 'Customer records keep invoicing details organized and reusable.',
          },
        },
      ],
    },
    {
      id: 'vendor-menu',
      title: 'Vendor Menu',
      body: [
        'The vendor area is used to manage supplier liabilities, reimbursements, payment execution, and vendor master records.',
      ],
      subsections: [
        {
          id: 'vendor-bills',
          title: 'Bills',
          body: [
            'Go to Vendors -> Bills.',
            'Bills are used to record bills received from vendors.',
            'Why we use it: to track company expenses and payable amounts.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image7.png',
            alt: 'Vendor bill form with invoice line and payment status',
            caption: 'Vendor bills record incoming supplier charges and support payable tracking.',
          },
        },
        {
          id: 'vendor-refunds',
          title: 'Refunds',
          body: [
            'Go to Vendors -> Refunds.',
            'Refunds are used to record refunds received from vendors.',
            'Why we use it: to adjust or reverse vendor bills.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image8.png',
            alt: 'Vendor refund form for reversing a vendor bill',
            caption: 'Vendor refunds adjust payables when goods are returned or charges are reversed.',
          },
        },
        {
          id: 'vendor-payments',
          title: 'Payments',
          body: [
            'Go to Vendors -> Payments.',
            'Payments record amounts paid to vendors.',
            'Why we use it: to track outgoing payments.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image9.png',
            alt: 'Vendor payment form showing amount and journal information',
            caption: 'Vendor payments show how supplier dues are settled and through which journal.',
          },
        },
        {
          id: 'employee-expenses',
          title: 'Employee Expenses',
          body: [
            'Go to Vendors -> Employee Expenses.',
            'Employee Expenses record expenses made by employees on behalf of the company.',
            'Why we use it: to reimburse employee spending.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/imagea.png',
            alt: 'Employee expense report with submitted and approved states',
            caption: 'Employee expense entries support reimbursement and payable tracking.',
          },
        },
        {
          id: 'bank-accounts',
          title: 'Bank Accounts',
          body: [
            'Go to Vendors -> Bank Accounts.',
            'This menu stores vendor bank details.',
            'Why we use it: to make secure and accurate payments.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/invoice/imagec.png',
              alt: 'Bank account record with account number and holder details',
              caption: 'Bank account records store the account details used during payment processing.',
            },
            {
              src: '/assets/doc-images/invoice/imagef.png',
              alt: 'Create a bank account dialog for invoice configuration',
              caption: 'New bank accounts can be added directly from the banking configuration workflow.',
            },
          ],
        },
        {
          id: 'vendors',
          title: 'Vendors',
          body: [
            'Go to Vendors -> Vendors.',
            'This menu stores supplier information.',
            'Why we use it: to manage vendor records and transactions.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/imageb.png',
            alt: 'Vendor kanban list with supplier records',
            caption: 'Vendor records organize supplier profiles and connect them to bills and payments.',
          },
        },
      ],
    },
    {
      id: 'configuration',
      title: 'Configuration',
      body: [
        'The Configuration area controls payment rules, accounting structures, online payment behavior, and master records used in invoicing.',
      ],
      subsections: [
        {
          id: 'payment-terms',
          title: 'Payment Terms',
          body: [
            'Go to Configuration -> Invoicing -> Payment Terms.',
            'Payment Terms define payment deadlines for invoices.',
            'Why we use it: to set due dates for payments.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/imaged.png',
            alt: 'Payment terms record with due rule preview',
            caption: 'Payment terms control when invoice amounts are expected to be paid.',
          },
        },
        {
          id: 'incoterms',
          title: 'Incoterms',
          body: [
            'Go to Configuration -> Invoicing -> Incoterms.',
            'Incoterms define international trade terms for delivery and shipping.',
            'Why we use it: to clarify responsibilities in shipping.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/imagee.png',
            alt: 'Incoterms list with trade codes',
            caption: 'Incoterms standardize delivery and shipping responsibility in cross-border transactions.',
          },
        },
        {
          id: 'add-bank-account',
          title: 'Add a Bank Account',
          body: [
            'Go to Configuration -> Banks -> Add a Bank Account.',
            'This option is used to add company bank account details.',
            'Why we use it: to manage transactions and payments.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/imagef.png',
            alt: 'Create a bank account form with account number and bank details',
            caption: 'Adding a bank account makes payment journals and settlements available to invoicing workflows.',
          },
        },
        {
          id: 'taxes',
          title: 'Taxes',
          body: [
            'Go to Configuration -> Accounting -> Taxes.',
            'Taxes define tax rules applied on invoices.',
            'Why we use it: to calculate tax automatically.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image10.png',
            alt: 'Tax record showing tax type and tax scope',
            caption: 'Tax configuration drives automatic tax computation on invoice lines.',
          },
        },
        {
          id: 'journals',
          title: 'Journals',
          body: [
            'Go to Configuration -> Accounting -> Journals.',
            'Journals record all accounting transactions.',
            'Why we use it: to categorize financial entries.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image11.png',
            alt: 'Journal list showing sales purchase and bank journals',
            caption: 'Journals group transactions by operational and accounting purpose.',
          },
        },
        {
          id: 'currencies',
          title: 'Currencies',
          body: [
            'Go to Configuration -> Accounting -> Currencies.',
            'Currencies manage multiple currencies.',
            'Why we use it: to handle international transactions.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image12.png',
            alt: 'Currencies list with active currency definitions',
            caption: 'Currency records support multi-currency invoicing and reporting.',
          },
        },
        {
          id: 'fiscal-positions',
          title: 'Fiscal Positions',
          body: [
            'Go to Configuration -> Accounting -> Fiscal Positions.',
            'Fiscal Positions define tax rules based on region or customer type.',
            'Why we use it: to apply correct taxes automatically.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image13.png',
            alt: 'Fiscal positions list used for region based tax mapping',
            caption: 'Fiscal positions automate tax application based on geography or tax treatment.',
          },
        },
        {
          id: 'journal-groups',
          title: 'Journal Groups',
          body: [
            'Go to Configuration -> Accounting -> Journal Groups.',
            'Journal Groups group multiple journals together.',
            'Why we use it: to organize accounting data.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image14.png',
            alt: 'Journal Groups list for grouped journals',
            caption: 'Journal groups organize related journals into reusable accounting sets.',
          },
        },
        {
          id: 'tax-groups',
          title: 'Tax Groups',
          body: [
            'Go to Configuration -> Accounting -> Tax Groups.',
            'Tax Groups group similar taxes together.',
            'Why we use it: to simplify tax reporting.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image15.png',
            alt: 'Tax groups list with grouped tax records',
            caption: 'Tax groups simplify reporting by combining similar taxes under shared headings.',
          },
        },
        {
          id: 'payment-providers',
          title: 'Payment Providers',
          body: [
            'Go to Configuration -> Online Payments -> Payment Providers.',
            'Payment Providers define online payment services.',
            'Why we use it: to accept online payments.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image16.png',
            alt: 'Payment provider catalog with installable gateways',
            caption: 'Payment providers connect the invoicing system to supported online payment gateways.',
          },
        },
        {
          id: 'payment-methods',
          title: 'Payment Methods',
          body: [
            'Go to Configuration -> Online Payments -> Payment Methods.',
            'Payment Methods define modes of payment.',
            'Why we use it: to specify how payments are made.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image17.png',
            alt: 'Payment method record for cash method',
            caption: 'Payment methods define the supported settlement modes available during payment processing.',
          },
        },
        {
          id: 'payment-tokens',
          title: 'Payment Tokens',
          body: [
            'Go to Configuration -> Online Payments -> Payment Tokens.',
            'Payment Tokens store saved payment details securely.',
            'Why we use it: to enable quick payments.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image18.png',
            alt: 'Payment tokens list showing no saved token records',
            caption: 'Payment tokens support saved credentials for faster payment experiences.',
          },
        },
        {
          id: 'payment-transactions',
          title: 'Payment Transactions',
          body: [
            'Go to Configuration -> Online Payments -> Payment Transactions.',
            'Payment Transactions record all payment operations.',
            'Why we use it: to track payment history.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image19.png',
            alt: 'Payment transactions list for online payment operations',
            caption: 'Payment transactions provide a historical record of processed payment operations.',
          },
        },
        {
          id: 'product-categories',
          title: 'Product Categories',
          body: [
            'Go to Configuration -> Management -> Product Categories.',
            'Product Categories group products into categories.',
            'Why we use it: to organize products efficiently.',
          ],
          figure: {
            src: '/assets/doc-images/invoice/image1a.png',
            alt: 'Product category record for education services',
            caption: 'Product categories organize invoiceable items into consistent financial groupings.',
          },
        },
      ],
    },
  ],
  related: ['purchase', 'expenses', 'inventory'],
};

export default function InvoicePage() {
  return <DocumentPage document={invoiceDocument} />;
}
